import {
  createRouter,
  createWebHistory,
  RouteRecordRaw,
  type LocationQueryRaw,
  type RouteRecordName,
  type Router,
} from "vue-router";
import { useBookingStore } from "@/store/bookingModule";
import { useSearchStore } from "@/store/searchModule";
import { updateBookingInfo } from "@/composables/booking/updateBookingInfoToken";
import {
  replaceVerivoxThemeQuery,
  routeQueryHasVerivoxTheme,
} from "@/utils/verivoxThemeQuery";
import { isIframeTestHarnessEnabled } from "@/utils/iframeTestHarnessEnv";
import {
  isKnownCalculatorAppSlug,
  normalizeRouteParam,
  resolveEnglishAppId,
  resolveGermanAppSlug,
} from "@/utils/calculatorAppMapping";
import { waitForAppsBootstrap } from "@/utils/waitForAppsBootstrap";
import { isAccessibilityToolsEnabled } from "@/utils/accessibilityTools";
import { isFromPortal } from "@/composables/fromPortal";
import { contractRenewalIdsMatch } from "@/utils/contractRenewalId";
import { requestEmbedScrollTop } from "@/utils/scrollToTopForModal";
import { rollbar } from "../../rollbar";

/** Route names whose `params.app` must exist in `window.covomoCalculatorsMappingToEn`. */
const ROUTES_REQUIRING_KNOWN_APP_SLUG = new Set([
  "Search",
  "Wizard",
  "StepByStep",
  "Comparison",
  "Tariff",
  "assessment",
  "Booking",
  "contractRenewal",
]);

type RoutePathParams = Record<string, string | number | null | undefined>;

const replaceRoutePathParams = (
  path: string,
  params: RoutePathParams = {},
): string => {
  return path.replace(/:([^/()]+)(\([^)]*\))?[+*?]?/g, (match, key) => {
    const value = params[key];
    if (value === null || value === undefined) return match;
    return encodeURIComponent(String(value));
  });
};

export const getLocalizedRoutePath = (
  router: Router,
  routeName: string,
  params: RoutePathParams = {},
  useAlias = false,
): string => {
  const routeRecords = router
    .getRoutes()
    .filter((routeRecord) => routeRecord.name === routeName);
  const routeRecord =
    routeRecords.find((record) =>
      useAlias ? Boolean(record.aliasOf) : !record.aliasOf,
    ) ?? routeRecords[0];

  if (!routeRecord) return "";

  return replaceRoutePathParams(routeRecord.path, params);
};

export const shouldUseLocalizedRouteAlias = (locale?: string): boolean => {
  return locale?.startsWith("en") === true;
};
const MobileUploadPage = () =>
  import(
    /* webpackChunkName: "MobileUploadPage" */ "@/views/MobileUploadPage.vue"
  );

const UUID_V4_PATTERN =
  "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-4[0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}";
export const getLocalizedRouteLocation = (
  router: Router,
  routeName: string,
  params: RoutePathParams = {},
  query: LocationQueryRaw = {},
  locale?: string,
) => {
  return {
    path: getLocalizedRoutePath(
      router,
      routeName,
      params,
      shouldUseLocalizedRouteAlias(locale),
    ),
    query,
  };
};

const SearchPage = () =>
  import(/* webpackChunkName: "SearchPage" */ "@/views/SearchPage.vue");
const BookingPage = () =>
  import(/* webpackChunkName: "BookingPage" */ "@/views/BookingPage.vue");
const ComparisonPage = () =>
  import(/* webpackChunkName: "ComparisonPage" */ "@/views/ComparisonPage.vue");
const VerificationPage = () =>
  import(
    /* webpackChunkName: "ComparisonPage" */ "@/views/VerificationPage.vue"
  );
const ConfirmCode = () =>
  import(/* webpackChunkName: "ComparisonPage" */ "@/views/ConfirmCode.vue");
const PageNotFound = () =>
  import(/* webpackChunkName: "PageNotFound" */ "@/views/PageNotFound.vue");
const WechselPilotPage = () =>
  import(
    /* webpackChunkName: "WechselPilotPage" */ "@/views/WechselPilotPage.vue"
  );
const NammertPage = () =>
  import(/* webpackChunkName: "NammertPage" */ "@/views/NammertPage.vue");
const WizardPage = () =>
  import(/* webpackChunkName: "WizardPage" */ "@/views/WizardPage.vue");
const StepByStepPage = () =>
  import(/* webpackChunkName: "StepByStepPage" */ "@/views/StepByStepPage.vue");
const TariffDetails = () =>
  import(/* webpackChunkName: "WizardPage" */ "@/views/TariffDetails.vue");
const TariffSearch = () =>
  import(/* webpackChunkName: "WizardPage" */ "@/views/TariffSearch.vue");
const ResumeBooking = () =>
  import(/* webpackChunkName: "WizardPage" */ "@/views/ResumeBooking.vue");
const ContractRenewalPage = () =>
  import(
    /* webpackChunkName: "ContractRenewalPage" */ "@/views/ContractRenewalPage.vue"
  );
const IframeParentTestPage = () =>
  import(
    /* webpackChunkName: "IframeParentTestPage" */ "@/views/IframeParentTestPage.vue"
  );
const AccessibilityStatementPage = () =>
  import(
    /* webpackChunkName: "AccessibilityStatementPage" */ "@/views/AccessibilityStatementPage.vue"
  );
const routes: Array<RouteRecordRaw> = [
  {
    path: "/barrierefreiheit",
    name: "AccessibilityStatement",
    component: AccessibilityStatementPage,
    alias: "/accessibility",
  },
  {
    path: "/",
    name: "Home",
    redirect: () => {
      return { path: "/reise" };
    },
  },
  {
    path: "/search/:app",
    name: "Depricated Search",
    redirect: (router) => {
      return { path: `/${router.params.app}` };
    },
  },
  {
    path: "/vergleich/:app",
    name: "Comparison",
    component: ComparisonPage,
    props: true,
    alias: "/comparison/:app",
  },
  {
    path: "/verifizieren",
    name: "Verification",
    component: VerificationPage,
    alias: "/verification",
  },
  {
    path: "/fortsetzen/:code",
    name: "resumeBooking",
    component: ResumeBooking,
    alias: "/continue/:code",
    props: false,
    beforeEnter: (to, from, next) => {
      if (from.name !== undefined) {
        return next();
      }
      return next({
        name: "confirmCode",
        params: {
          code: to.params.code,
        },
        query: to.query,
        replace: true,
      });
    },
  },
  {
    path: `/:token(${UUID_V4_PATTERN})`,
    alias: `/mobile-upload/:token(${UUID_V4_PATTERN})`,
    name: "MobileUpload",
    component: MobileUploadPage,
    props: true,
  },
  {
    path: "/berechnung/:code/:verificationCode?",
    name: "confirmCode",
    component: ConfirmCode,
    alias: "/calculation/:code/:verificationCode?",
    props: false,
  },
  {
    path: "/antrag/:app/:tariff_id",
    name: "Booking",
    props: true,
    component: BookingPage,
    alias: "/booking/:app/:tariff_id",
    beforeEnter: async (to, from, next) => {
      if (from.name !== undefined) {
        return next();
      }

      await waitForAppsBootstrap();
      const app = resolveEnglishAppId(to.params.app);
      const routeAppSlug = normalizeRouteParam(to.params.app);
      // Prefer mapped DE slug; fall back to URL param when it is already a known slug.
      const germanSlug =
        resolveGermanAppSlug(app) ||
        (routeAppSlug && isKnownCalculatorAppSlug(routeAppSlug)
          ? routeAppSlug
          : undefined);
      if (!germanSlug) {
        return next({ path: "/404", replace: true });
      }

      if (to.name === "Booking") {
        return next({
          path: `/${germanSlug}`,
          query: {
            ...to.query,
          },
        });
      }
      return next(`/${germanSlug}`);
    },
  },
  {
    path: "/404",
    name: "NotFound",
    component: PageNotFound,
  },
  {
    path: "/__covomo_iframe_test__",
    name: "IframeParentTest",
    component: IframeParentTestPage,
    beforeEnter(_to, _from, next) {
      if (!isIframeTestHarnessEnabled()) {
        return next({ path: "/404", replace: true });
      }
      return next();
    },
  },
  {
    path: "/ext/boot",
    props: true,
    name: "nammet",
    component: NammertPage,
  },
  {
    path: "/ext/:app",
    name: "external",
    props: true,
    component: WechselPilotPage,
  },
  {
    path: "/:app/vertrag-erneuern",
    name: "contractRenewal",
    component: ContractRenewalPage,
    props: true,
    alias: "/:app/contract-renewal",
  },
  {
    path: "/:app",
    name: "Search",
    component: SearchPage,
    props: true,
    beforeEnter: (to, _from, next) => {
      if (!Object.prototype.hasOwnProperty.call(to.query, "vertrag_erneuern")) {
        return next();
      }

      const rawRenewal = to.query.vertrag_erneuern;
      const contractRenewal = Array.isArray(rawRenewal)
        ? String(rawRenewal[0] ?? "")
        : String(rawRenewal ?? "");

      if (!contractRenewal || isFromPortal(to.query)) {
        return next();
      }

      const validRenewal = sessionStorage.getItem("valid_renewal");
      if (
        validRenewal &&
        contractRenewalIdsMatch(validRenewal, contractRenewal)
      ) {
        return next();
      }

      return next({
        name: "contractRenewal",
        params: { app: to.params.app },
        query: to.query,
        replace: true,
      });
    },
  },
  {
    path: "/bedarf/:app",
    name: "Wizard",
    component: WizardPage,
    props: true,
    alias: "/wizard/:app",
  },
  {
    path: "/sbs/:app",
    name: "StepByStep",
    component: StepByStepPage,
    props: true,
  },
  {
    path: "/:app/:tariff_id/detail",
    name: "Tariff",
    component: TariffDetails,
    props: true,
  },
  {
    path: "/:app/:tariff_id/detail/risikopruefung",
    name: "assessment",
    component: TariffSearch,
    props: true,
    alias: "/:app/:tariff_id/detail/assessment",
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    // Product expectation: every real navigation starts at the top (search ↔
    // comparison ↔ booking). history.scrollRestoration is manual in main.ts.
    // Embed parent is notified via requestEmbedScrollTop in afterEach.
    return { left: 0, top: 0 };
  },
});

router.beforeEach(async (to, from, next) => {
  if (to.matched.length < 1) return next("/404");

  if (to.name === "AccessibilityStatement" && !isAccessibilityToolsEnabled()) {
    return next({ path: "/", replace: true });
  }

  if (routeQueryHasVerivoxTheme(to.query)) {
    return next({
      path: to.path,
      query: replaceVerivoxThemeQuery(to.query),
      hash: to.hash,
      replace: true,
    });
  }

  const routeName = to.name as string | undefined;
  if (
    routeName &&
    ROUTES_REQUIRING_KNOWN_APP_SLUG.has(routeName) &&
    to.params.app != null
  ) {
    await waitForAppsBootstrap();
  }
  if (
    routeName &&
    ROUTES_REQUIRING_KNOWN_APP_SLUG.has(routeName) &&
    to.params.app != null &&
    !isKnownCalculatorAppSlug(to.params.app)
  ) {
    return next({ path: "/404", replace: true });
  }

  const app = resolveEnglishAppId(to.params.app);
  const forbiddenNames: Array<RouteRecordName | null | undefined> = [
    undefined,
    "Comparison",
  ];
  // if (to.name === "Search" && !forbiddenNames.includes(from.name)) {
  //   try {
  //     const bookingStore = useBookingStore();
  //     updateBookingInfo(bookingStore, app, useSearchStore().bookingToken);
  //   } catch (e) {
  //     // Failed to update booking info
  //   }
  // }
  if (to.name === "assessment" && app && !forbiddenNames.includes(from.name)) {
    try {
      const bookingStore = useBookingStore();
      updateBookingInfo(bookingStore, app, useSearchStore().bookingToken);
    } catch (e) {
      // Failed to update booking info
      rollbar?.error(e as Error, { context: "router:updateBookingInfo" });
    }
  }
  return next();
});

/** Do not carry booking inline / toast messages across real page changes. */
router.afterEach((to, from, failure) => {
  if (failure) return;
  const sameRouteView =
    String(to.name ?? "") === String(from.name ?? "") && to.path === from.path;
  if (sameRouteView) return;

  const bookingStore = useBookingStore();
  bookingStore.setToastVisibility(false);
  bookingStore.setToastOptions({
    content: "",
    type: "",
    offerJumpToFirstError: false,
  });

  // Standalone, classic iframe, and modern embed hosts: always jump to top on
  // real route changes (search ↔ booking, wizard, …).
  requestEmbedScrollTop({ behavior: "auto" });
});

export default router;
